import {
  TellescopeModelFromName,
  useTellescopeQueries,
  useTellescopeQuery,
  useTellescopeInfiniteQuery,
} from "@tellescope/tanstack-query";
import { formatDateTime } from "@tellescope/utils";
import { FormAnswer } from "../../ContactsDialog/sections/Forms/FormAnswer";
import { useTranslation } from "react-i18next";
import { FileText, ChevronDown, ChevronRight } from "lucide-react";
import { useState, useMemo } from "react";
import { SinglePageFormSubmission } from "../../ContactsDialog/sections/Forms/SinglePageFormSubmission";

type Response = TellescopeModelFromName<"form_responses">;
type Form = TellescopeModelFromName<"forms">;

export interface OtherFormsContextCardProps {
  ticketId: string;
  formIds: string[];
}

export function OtherFormsContextCard({ ticketId, formIds }: OtherFormsContextCardProps) {
  const { t } = useTranslation("forms");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [editingResponseId, setEditingResponseId] = useState<string | null>(null);
  const [submittingRelatedForId, setSubmittingRelatedForId] = useState<string | null>(null);

  // Get the ticket to find the enduser
  const ticketQuery = useTellescopeQuery({
    model: "tickets",
    method: "getOne",
    request: ticketId,
    enabled: !!ticketId,
  });

  const enduserId = ticketQuery.data?.enduserId;

  // Get all forms
  const formsQueries = useTellescopeQueries({
    model: "forms",
    method: "getOne",
    requests: formIds,
    enabled: !!formIds?.length,
  });

  // Get all form responses for this enduser
  const formResponsesQuery = useTellescopeInfiniteQuery({
    model: "form_responses",
    method: "getSome",
    request: {
      filter: { enduserId },
    },
    select: (data) => data.pages.flat(),
    enabled: !!enduserId,
  });

  // Filter responses to only include those for the specified form IDs
  const filteredResponses = useMemo(() => {
    if (!formResponsesQuery.data) return [];
    return formResponsesQuery.data.filter((response) => formIds.includes(response.formId));
  }, [formResponsesQuery.data, formIds]);

  const isLoading = ticketQuery.isLoading || formsQueries.some((q) => q.isLoading) || formResponsesQuery.isLoading;
  const hasError = ticketQuery.isError || formsQueries.some((q) => q.isError) || formResponsesQuery.isError;

  // Build a map of formId => form
  const formsMap = useMemo(() => {
    return formsQueries.reduce<Record<string, Form>>((acc, query) => {
      if (query.data) {
        acc[query.data.id] = query.data;
      }
      return acc;
    }, {});
  }, [formsQueries]);

  // Group responses by formId
  const responsesByFormId = useMemo(() => {
    const map: Record<string, Response[]> = {};
    filteredResponses.forEach((response) => {
      if (!map[response.formId]) {
        map[response.formId] = [];
      }
      const bucket = map[response.formId];
      if (bucket) {
        bucket.push(response);
      }
    });
    return map;
  }, [filteredResponses]);

  const closeFormSubmission = () => {
    setEditingResponseId(null);
    setSubmittingRelatedForId(null);
  };

  if (isLoading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p className="text-sm">{t("Loading form responses...")}</p>
      </div>
    );
  }

  if (hasError) {
    return (
      <div className="p-4 border border-destructive rounded-lg">
        <p className="text-sm text-destructive">{t("Unable to load form responses. Please try again.")}</p>
      </div>
    );
  }

  const toggleExpanded = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // If we're editing or submitting related, show the form submission
  const editingResponse = filteredResponses.find((r) => r.id === editingResponseId);
  const submittingRelatedResponse = filteredResponses.find((r) => r.id === submittingRelatedForId);

  if (editingResponse || submittingRelatedResponse) {
    const response = editingResponse || submittingRelatedResponse;
    if (!response || !enduserId) return null;

    return (
      <SinglePageFormSubmission
        onSubmit={closeFormSubmission}
        onSaveResponse={closeFormSubmission}
        onCancel={closeFormSubmission}
        contactId={enduserId}
        responseId={editingResponse ? response.id : undefined}
        parentResponseId={submittingRelatedResponse ? response.id : undefined}
        rootResponseId={submittingRelatedResponse ? response.rootResponseId : undefined}
        formId={editingResponse ? response.formId : undefined}
      />
    );
  }

  // Count total responses
  const totalResponses = filteredResponses.length;

  return (
    <div className="space-y-2 border border-gray-200 rounded-lg bg-white overflow-hidden">
      <div className="flex items-center justify-between p-4 bg-gray-200 border-b border-b-gray-200">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <FileText className="h-4 w-4 text-gray-600 flex-shrink-0" />
          <h3 className="text-sm font-medium text-gray-900 truncate">{t("Context Forms")}</h3>
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-500 flex-shrink-0">
          <span>
            {t("{{count}} response", {
              count: totalResponses,
              defaultValue_one: "{{count}} response",
              defaultValue_other: "{{count}} responses",
            })}
          </span>
        </div>
      </div>

      <div className="space-y-2 p-4">
        {formIds.map((formId) => {
          const form = formsMap[formId];
          if (!form) return null;
          const responses = responsesByFormId[formId] || [];

          return (
            <div key={formId} className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 p-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-gray-600 flex-shrink-0" />
                  <span className="text-sm font-medium text-gray-900">
                    {form.title.toLowerCase().trim().endsWith("form")
                      ? form?.title || t("Form")
                      : t("{{formTitle}} Form", { formTitle: form?.title ?? t("Form") })}
                  </span>
                  <span className="text-xs text-gray-500">
                    ({responses.length} {responses.length === 1 ? t("response") : t("responses")})
                  </span>
                </div>
              </div>

              {responses.length > 0 ? (
                <div className="divide-y divide-gray-200">
                  {responses.map((response) => {
                    const isExpanded = expandedIds.has(response.id);

                    return (
                      <div key={response.id}>
                        <button
                          onClick={() => toggleExpanded(response.id)}
                          className="w-full flex items-center justify-between p-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            )}
                            <span className="text-sm text-gray-700 truncate">{t("Response")}</span>
                          </div>
                          <span className="text-xs text-gray-500 flex-shrink-0">
                            {formatDateTime(response.createdAt)}
                          </span>
                        </button>

                        {isExpanded && (
                          <div className="border-t border-gray-200 bg-white">
                            <div className="flex flex-col gap-3 p-3">
                              {response.responses?.length ? (
                                response.responses.map((item) => (
                                  <FormAnswer title={item.fieldTitle} answer={item.answer} key={item.fieldId} />
                                ))
                              ) : (
                                <div className="text-center py-4 text-muted-foreground text-sm">
                                  {t("No response data available")}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="p-4 text-center text-sm text-gray-500">{t("No responses for this form")}</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
